import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { addDays, startOfWeek, format } from "date-fns";
import { AvailabilityForm } from "./availability-form";
import { useUpdateAvailability } from "../../api/update-availability";

export const AvailabilityUpdate = ({ availability }) => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

    const daysOfWeek = Array.from({ length: 7 }, (_, i) =>
        addDays(weekStart, i),
    );

    const days = daysOfWeek.map((day) => format(day, "EEE")); // Mon, Tue, Wed etc

    const defaultValues = days.reduce((acc, day) => {
        const sortAsc = (a, b) => a.startTime.localeCompare(b.startTime);

        const items = availability
            .filter(
                (x) =>
                    x.dayOfWeek === day &&
                    !x.isDeleted &&
                    x.startDate === "1900-01-01" &&
                    x.endDate === "2097-01-01",
            )
            .sort(sortAsc);

        if (items.length) {
            // Set switch state, enabled if start/end time is not 00:00
            acc[day] = {
                enabled: items.some(
                    (item) =>
                        item.startTime !== "00:00" || item.endTime !== "00:00",
                ),
                Times: items.map((item) => {
                    const isOpen =
                        item.startTime !== "00:00" || item.endTime !== "00:00";

                    return {
                        availabilityGuid: item.availabilityGuid,
                        startTime: isOpen ? item.startTime : "",
                        endTime: isOpen ? item.endTime : "",
                    };
                }),
                applyTo: [day],
            };
        } else {
            // No active record exists for day (or is deleted)
            // Provide a blank slot so form can render a slot when switch is on
            acc[day] = {
                enabled: false,
                Times: [
                    {
                        availabilityGuid: "",
                        startTime: "",
                        endTime: "",
                    },
                ],
                applyTo: [day],
            };
        }

        return acc;
    }, {});

    const timeSlotSchema = yup.object({
        startTime: yup.string().required("Start time is required"),
        endTime: yup
            .string()
            .required("End time is required")
            .test(
                "is-after-start",
                "End time must be after start time",
                function (endTime) {
                    const { startTime } = this.parent;

                    return !startTime || !endTime || endTime > startTime;
                },
            ),
    });

    const schema = yup.object(
        days.reduce((acc, day) => {
            acc[day] = yup.object({
                enabled: yup.boolean(),
                Times: yup.array().when("enabled", {
                    is: true,
                    then: (schema) =>
                        schema
                            .min(1, "Please add at least one availability slot")
                            .of(timeSlotSchema)
                            .test(
                                "no-overlap",
                                "Time slots cannot overlap",
                                function (times) {
                                    if (!times || times.length < 2) return true;

                                    // Only consider complete slots
                                    const valid = times
                                        .map((t, index) => ({ ...t, index }))
                                        .filter(
                                            (t) => t.startTime && t.endTime,
                                        );

                                    const sorted = [...valid].sort((a, b) =>
                                        a.startTime.localeCompare(b.startTime),
                                    );

                                    for (let i = 1; i < sorted.length; i++) {
                                        const prev = sorted[i - 1];
                                        const curr = sorted[i];

                                        if (curr.startTime < prev.endTime) {
                                            // attach the error to final pair
                                            const target =
                                                curr.index > prev.index
                                                    ? curr
                                                    : prev;

                                            return this.createError({
                                                path: `${this.path}[${target.index}].startTime`,
                                                message:
                                                    "Time slots cannot overlap",
                                            });
                                        }
                                    }

                                    return true;
                                },
                            ),
                    otherwise: (schema) => schema.notRequired(),
                }),
            });
            return acc;
        }, {}),
    );

    const methods = useForm({
        resolver: yupResolver(schema),
        values: defaultValues,
        mode: "onChange",
    });

    const mutation = useUpdateAvailability();

    const onSubmit = (data) => {
        mutation.mutate({
            data,
            days,
        });
    };

    return (
        <>
            <h2 className="mb-3 text-xl font-semibold text-darkgreen-500">
                Regular schedule
            </h2>
            <AvailabilityForm
                methods={methods}
                onSubmit={onSubmit}
                daysOfWeek={daysOfWeek}
                isPending={mutation.isPending}
            />
        </>
    );
};
